// 🔒
import { useEffect, useMemo, useState } from "react";
import { programs } from "../data/programs";

const AUTOPLAY_DELAY = 8000;
const featuredPrograms = programs.filter((program) => program.featured);

function getVisibleCount() {
  if (typeof window === "undefined") return 3;
  return window.innerWidth <= 768 ? 1 : 3;
}

export default function ProgramsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(getVisibleCount());
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleCount());
      setActiveIndex(0);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxIndex = Math.max(0, featuredPrograms.length - visibleCount);

  const nextSlide = () => {
    setAutoplayEnabled(false);
    setActiveIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setAutoplayEnabled(false);
    setActiveIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goToSlide = (index) => {
    setAutoplayEnabled(false);
    setActiveIndex(index);
  };

  useEffect(() => {
    if (!autoplayEnabled || featuredPrograms.length <= visibleCount) return;

    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, AUTOPLAY_DELAY);

    return () => clearInterval(timer);
  }, [autoplayEnabled, maxIndex, visibleCount]);

  const pages = useMemo(() => {
    return Array.from({ length: maxIndex + 1 }, (_, i) => i);
  }, [maxIndex]);

  return (
    <section className="section programs-section" id="programs">
      <div className="container">
        <div className="section-heading programs-section__heading">
          <p className="eyebrow">PROGRAMS</p>
          <h2>Programs available in the app</h2>
          <p className="section-copy">
            Explore structured training plans built for different goals, levels,
            and coaching styles.
          </p>
        </div>

        <div className="programs-carousel">
          <div className="programs-viewport">
            <div
              className="programs-track"
              style={{
                transform: `translateX(-${activeIndex * (100 / visibleCount)}%)`
              }}
            >
              {featuredPrograms.map((program) => (
                <article
                  className="program-card"
                  key={program.id}
                  style={{ flex: `0 0 ${100 / visibleCount}%` }}
                >
                  <div className="program-card__inner">
                    <div className="program-card__media">
                      <img
                        src={program.image}
                        alt={program.title}
                        className="program-card__image"
                      />

                      <div className="program-card__overlay">
                        <span className="program-pill">{program.category}</span>
                        <span className="program-pill">{program.level}</span>
                      </div>
                    </div>

                    <div className="program-card__content">
                      <h3>{program.title}</h3>
                      <p>{program.description}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className="programs-controls">
          <button
            className="programs-nav"
            onClick={prevSlide}
            aria-label="Previous programs"
          >
            ‹
          </button>

          <div className="programs-dots">
            {pages.map((pageIndex) => (
              <button
                key={pageIndex}
                className={`programs-dot ${
                  pageIndex === activeIndex ? "programs-dot--active" : ""
                }`}
                onClick={() => goToSlide(pageIndex)}
                aria-label={`Go to program slide ${pageIndex + 1}`}
              />
            ))}
          </div>

          <button
            className="programs-nav"
            onClick={nextSlide}
            aria-label="Next programs"
          >
            ›
          </button>
        </div>
      </div>
    </section>
  );
}
// 🔒
